import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall } from '@reducers/utils';
import { Alert } from '@helpers';
import type { RootState, AppThunk } from '@store/types';
import type { ApiError } from '@reducers/utils/types';
import type { ToolsState, Tool, StoreToolResponse, ToolSearchResponse, StoreToolRequestDto, DeleteToolRequestDto } from './types';

// Re-export types for consumers
export type { ToolsState, Tool, StoreToolResponse } from './types';

// === Initial State ===

const initState: ToolsState = {
  tools: [],
  isMore: true,
  loaded: false,
  pageNumber: 0,
  pageSize: 20,
};

// === Actions ===

export const clear = createAction('clear');
export const close = createAction('tools/close');
export const updateTools = createAction<Tool[]>('tools/updateTools');
export const setTools = createAction<Tool[]>('tools/setTools');
export const setIsMore = createAction<boolean>('tools/setIsMore');
export const updatePage = createAction<number | undefined>('tools/updatePage');

// === Thunks ===

export const getTools = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.tools.get, {
        onSuccess: (result: Tool[]) => {
          dispatch(setTools(result));
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const getToolById = (id: number): AppThunk<Promise<ToolSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.toolsSearch, {
        params: {
          top: 1,
          skip: 0,
          oDataFilter: `ToolId eq ${id}`,
        },
      })
    )
  );
};

export const create = (data: StoreToolRequestDto): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.tools.store, {
        params: data,
        onSuccess: () => {},
        onError: (error) => {
          console.log(error, 'error');
          Alert({ title: (error as ApiError)?.responseException?.exceptionMessage || error, type: 'danger' });
        },
      })
    )
  );
};

export const edit = (data: StoreToolRequestDto): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const {
    tools: { tools },
  } = getState();

  await dispatch(
    backendCall(
      createApiCall((api) => api.tools.store, {
        params: data,
        onSuccess: () => {
          console.log('success');
          const newTools = tools.map((t) => {
            if (t.toolId === data.toolId) {
              return {
                ...t,
                ...data,
              };
            } else {
              return t;
            }
          });
          dispatch(updateTools(newTools));
        },
        onError: (error) => {
          console.log(error, 'error');
          Alert({ title: (error as ApiError)?.responseException?.exceptionMessage || error, type: 'danger' });
        },
      })
    )
  );
};

export const remove = (data: DeleteToolRequestDto): AppThunk<Promise<StoreToolResponse | undefined>> => async (dispatch, getState) => {
  const {
    tools: { tools },
  } = getState();
  return await dispatch(
    backendCall(
      createApiCall((api) => api.tools.remove, {
        params: data,
        onSuccess: () => {
          const newTools = tools.filter((t) => t.toolId !== data.toolId);
          dispatch(updateTools(newTools));
        },
        onError: (error) => {
          console.log(error, 'error');
          Alert({ title: (error as ApiError)?.responseException?.exceptionMessage || error, type: 'danger' });
        },
      })
    )
  );
};

// === Reducer ===

export default createReducer<ToolsState>(JSON.parse(JSON.stringify(initState)), (builder) => {
  builder
    .addCase(clear, () => initState)
    .addCase(close, () => initState)
    .addCase(updatePage, (state, action) => ({
      ...state,
      pageNumber: action.payload ? action.payload : state.pageNumber + 1,
    }))
    .addCase(setIsMore, (state, action) => ({
      ...state,
      isMore: action.payload,
    }))
    .addCase(setTools, (state, action) => ({
      ...state,
      tools: action.payload,
      loaded: true,
    }))
    .addCase(updateTools, (state, action) => ({
      ...state,
      tools: action.payload,
    }));
});
